import {noop, objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {setWindowTitle} from 'BenchlingUtil';
import BufferOptions from 'buffers/BufferOptions';
import {ActionOrigin, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {accessibleObjs, getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL, {clearNavigationWarnings} from 'lib/navigateToURL';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureArchiveUnarchiveEventMetadata,
  composeProcedureTypeMetadata,
  PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT,
  PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT,
  PROCEDURE_MOVE_ENDED_EVENT,
  PROCEDURE_MOVE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import {
  addMethodToLinearFlowchart,
  AppendDirection,
  generateNewMethodCid,
  getMaterialFlows,
} from 'procedures/design/flowchartManagementUtils';
import getBannerForReadOnlyProcedure from 'procedures/design/getBannerForReadOnlyProcedure';
import {MethodEditorTab} from 'procedures/design/method-config/MethodConfigConstants';
import ProcedureEditor from 'procedures/design/ProcedureEditor';
import ProcedureEditorHeader from 'procedures/design/ProcedureEditorHeader';
import useEditingMethodNameState from 'procedures/design/useEditingMethodNameState';
import usePreSaveImmediatelyMiddleware from 'procedures/design/usePreSaveImmediatelyMiddleware';
import determineProcedureAccessLevel, {
  ProcedureAccessLevel,
} from 'procedures/design/validation/determineProcedureAccessLevel';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import parseProcedureFromNetwork from 'procedures/network/parseProcedureFromNetwork';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import serializeProcedureFlowchartForNetwork from 'procedures/network/serializeProcedureFlowchartForNetwork';
import {
  trackMovedProcedureEvent,
  trackToggledProcedureViewEvent,
} from 'procedures/procedureAnalyticsEventsUtil';
import {
  MaterialFlow,
  ProcedureFlowchart,
  ProcedureMetadata,
  ProcedureWithMetadata,
} from 'procedures/ProcedureDomainTypes';
import {normalizeFlowchartOrderingForComparison} from 'procedures/procedurePagePersistenceUtils';
import {ProcedureProcessesPlugin} from 'procedures/procedures-platform/ProcedureProcessesPlugin';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import AutosaveManager from 'procedures/redux/autosaveManager';
import {saveHandlerFactory} from 'procedures/redux/proceduresSaveHandler';
import duplicateProcedure from 'procedures/utils/duplicateProcedure';
import flushDocumentStore from 'procedures/utils/flushDocumentStore';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {
  useAllTagSchemasWithProceduresShape,
  useAllTagSchemasWithStandardShape,
} from 'procedures/utils/useAllTagSchemas';
import useBioentitiesForProcedures, {
  getBioentityIdsFromFlowchart,
} from 'procedures/utils/useBioentitiesForProcedures';
import ProcessFlowchartConfigEditor from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditor';
import {PublishingRecordForVersionHistoryPanel} from 'template-collectibles/publishing-ui/graphql/types';
import getTestProps from 'test/lib/cypress/getTestProps';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import bufferForUX from 'util/bufferForUX';
import * as logging from 'util/Logging';
import memoizeLastCall from 'util/memoizeLastCall';
import {withLoadingScreen} from 'util/Promises';

import {findNewValidationErrors} from './design/validation/procedureValidationUtils';
import {isNameEmpty, validateProcedure} from './design/validation/validateProcedure';
import {trackEncounteredConfigurationValidationError} from './procedureAnalyticsEventsUtil';
import {
  GetCachedCidFunctionFactory,
  IdToCidMap,
  INACCESSIBLE_FLOWCHART_VERSION_ID,
  LoadedFlowchartVersionMetadata,
  NetworkProcedure,
  ProcedureAutosaveManager,
  ProcedureCapabilityChecker,
  ProcedureFlowchartStateManager,
  ProcedureFlowchartVersionLoader,
  ProcedureHardRefreshManager,
  ProcedureLoader,
  ProcedureVersionHistoryManager,
  useProcedurePageIdToCidMap,
} from './ProcedurePageComponents';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  PersistenceInfo,
  ProcedurePageContext,
  ProcedurePageContextValue,
} from './ProcedurePageContext';

interface ProcedurePageProps {
  procedureId: string;
  flowchartVersionId?: string;
}

export type ModifyFlowchartCallback = (fc: ProcedureFlowchart) => ProcedureFlowchart;

export default function ProcedurePage({
  procedureId,
  flowchartVersionId,
}: ProcedurePageProps): JSX.Element | null {
  /**
   * The {key} props in the components below are specific and intentional, to ensure that
   * we keep existing component instances whenever we can.
   */
  return (
    <ProcedureAutosaveManager procedureId={procedureId}>
      <ProcedureHardRefreshManager>
        {(hardRefresh, refreshKey) => (
          // First: load the procedure (without the flowchart)
          <ProcedureLoader procedureId={procedureId} key={refreshKey}>
            {(thinProcedureData, refetchProcedure) => (
              // Next: the version history mgr tracks old versions, which one is visible, manages the URL
              <ProcedureVersionHistoryManager
                procedureId={procedureId}
                latestVersionId={
                  !isInaccessible(thinProcedureData.procedureFlowchart.latestFlowchartVersion)
                    ? thinProcedureData.procedureFlowchart.latestFlowchartVersion.id
                    : INACCESSIBLE_FLOWCHART_VERSION_ID
                }
                flowchartVersionId={flowchartVersionId}
                publishedFlowchartVersionId={
                  thinProcedureData.procedureFlowchart.publishedFlowchartVersion?.id
                }
                publishingRecords={accessibleObjs(thinProcedureData.publishingRecords)}
              >
                {(versionIdToView, setVersionIdToView) => (
                  // Next: ensure we have sufficient capabilities to view the procedure
                  <ProcedureCapabilityChecker procedureData={thinProcedureData}>
                    {/* Next: load the requested flowchart version (or, if not provided, the latest version) */}
                    <ProcedureFlowchartVersionLoader
                      procedureData={thinProcedureData}
                      procedureFlowchartVersionId={versionIdToView}
                      key={versionIdToView}
                    >
                      {(
                        fullProcedureData,
                        refetchFlowchartVersion,
                        loadedFlowchartVersion,
                        isViewingOldVersion
                      ) => (
                        // Finally, load the write manager, which also renders the procedure page
                        <ProcedurePageWriteManager
                          procedureGraphQL={fullProcedureData}
                          refetch={() => refetchFlowchartVersion(refetchProcedure)}
                          procedureFlowchartVersion={loadedFlowchartVersion}
                          isViewingOldVersion={isViewingOldVersion}
                          hardRefresh={hardRefresh}
                          onSwitchVersionId={setVersionIdToView}
                        />
                      )}
                    </ProcedureFlowchartVersionLoader>
                  </ProcedureCapabilityChecker>
                )}
              </ProcedureVersionHistoryManager>
            )}
          </ProcedureLoader>
        )}
      </ProcedureHardRefreshManager>
    </ProcedureAutosaveManager>
  );
}

// Convert {cid: id} into {id: cid}
function invertStringMapping(cidToIdMap: Record<string, string>): Record<string, string> {
  return objectFromEntries(Object.entries(cidToIdMap).map(([cid, id]) => [id, cid]));
}

/**
 * This function performs a "clean" save: it doesn't push any values into state or redux. It just calls the API.
 */
async function saveProcedure(
  procedure: ProcedureWithMetadata,
  persistenceInfo?: PersistenceInfo,
  lastSavedFlowchartVersionId?: string
): Promise<Record<string, string>> {
  const {
    metadata: {name, id, templateCollectionId},
  } = procedure;

  let updatedId: string = '';
  let cidToIdMap: Record<string, string> = {};

  const result = await ProcedureDAO.updateProcedure({
    id,
    name,
    flowchart: serializeProcedureFlowchartForNetwork(
      procedure.flowchart,
      procedure.metadata.procedureType,
      persistenceInfo,
      lastSavedFlowchartVersionId
    ),
    templateCollectionId,
  });

  updatedId = result.updatedIds[0];
  cidToIdMap = result.cidToIdMap;

  assert(id === updatedId, 'updated procedure id does not match provided id');
  return cidToIdMap;
}

/**
 * When we unmount, we need to curry in the lastSavedFlowchartVersionId, to ensure it's provided correctly
 */
function saveProcedureWhenUnmountedFactory(
  lastSavedFlowchartVersionId: string
): (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => Promise<null> {
  let saved = false;

  return async (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => {
    if (saved) {
      // Ensure that we don't save the procedure multiple times after it's been unmounted
      return null;
    }
    await saveProcedure(procedure, persistenceInfo, lastSavedFlowchartVersionId);
    saved = true;

    // Clean up the document store, to ensure we don't have any stale IDs. Without this cleanup,
    // it's fairly easy to end up with a server error (mismatched version) the next time we autosave.
    flushDocumentStore(procedure);
    return null;
  };
}

type UpdateProcedure = (
  procedure: ProcedureWithMetadata,
  persistenceInfo?: PersistenceInfo
) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;

const memoizedParseProcedureFromNetwork = memoizeLastCall(
  (procedureGraphQL: NetworkProcedure, getCachedCidFactory: GetCachedCidFunctionFactory) =>
    parseProcedureFromNetwork(procedureGraphQL, getCachedCidFactory()),
  {useDeepEquality: true}
);

const memoizedGetCachedCidFactory = memoizeLastCall(
  (map: IdToCidMap, getCachedCidFactory: GetCachedCidFunctionFactory) => () => getCachedCidFactory(map),
  {
    useDeepEquality: true,
  }
);

/** @package Exported for testing */
export function ProcedurePageWriteManager(props: {
  procedureGraphQL: NetworkProcedure;
  procedureFlowchartVersion: LoadedFlowchartVersionMetadata;
  isViewingOldVersion: boolean;
  onSwitchVersionId: (versionId: string) => void;
  refetch: () => Promise<NetworkProcedure | null> | null;
  hardRefresh: () => void;
}): JSX.Element {
  const isMountedRef = useIsNotUnmounted();
  // const isMountedValue = isMounted.current;
  // The last known flowchart version per-client that we will be procedure updates on top of
  // This will be used by the server to determine if the session is using a stale version of the procedure
  // To prevent concurrent edits from overwriting each other
  const lastSavedFlowchartVersionId = useRef<string>(
    getAccessibleOrInaccessibleId(props.procedureGraphQL.procedureFlowchart.latestFlowchartVersion)
  );
  const [fullyBlockedOnServerError, setFullyBlockedOnServerError] = useStateIfMounted(false);
  const {idToCidMap, setIdToCidMap, getCachedCidFactory} = useProcedurePageIdToCidMap(props.procedureGraphQL);
  const currentProcedure = useMemo(
    () => parseProcedureFromNetwork(props.procedureGraphQL, getCachedCidFactory()),
    [props.procedureGraphQL, getCachedCidFactory]
  );
  const {refetch} = props;

  const updateProcedure: UpdateProcedure = useCallback(
    async (procedure: ProcedureWithMetadata, persistenceInfo?: PersistenceInfo) => {
      let cidToIdMap;
      try {
        cidToIdMap = await saveProcedure(procedure, persistenceInfo, lastSavedFlowchartVersionId.current);
      } catch (e) {
        if (e.status === 409) {
          // Any error that should force the procedure into a read-only state that is recovered on refresh
          // Should be handled here. Currently this handles 409 state conflicts.
          setFullyBlockedOnServerError(true);
        }
        throw e;
      }

      const newIdToCidMap = invertStringMapping(cidToIdMap);

      // The next render cycle triggered by this setState call will NOT see the most up-to-date refetched ids
      setIdToCidMap((cachedIdToCidMap) => ({...cachedIdToCidMap, ...newIdToCidMap}));
      // The next render cycle triggered by this refetch call will see the most up-to-date refetched ids
      const updatedNetworkProcedure = await refetch();
      // The next render cycle triggered by this setState call will see the most up-to-date refetched ids
      setIdToCidMap(newIdToCidMap);

      if (isMountedRef.current) {
        // The call to `refetch` may return null. This happens if the component has been unmounted in the interim.
        // In that case, the return value here isn't important.
        let updatedProcedure = procedure;
        if (updatedNetworkProcedure != null) {
          assert(
            !isInaccessible(updatedNetworkProcedure.procedureFlowchart.latestFlowchartVersion),
            'Expected latest flowchart version to be accessible after write'
          );
          lastSavedFlowchartVersionId.current =
            updatedNetworkProcedure.procedureFlowchart.latestFlowchartVersion.id;
          updatedProcedure = memoizedParseProcedureFromNetwork(
            updatedNetworkProcedure,
            memoizedGetCachedCidFactory(newIdToCidMap, getCachedCidFactory)
          );
        }

        return {
          updatedProcedure,
          cidToIdMap,
        };
      } else {
        return null;
      }
    },
    [setIdToCidMap, getCachedCidFactory, refetch, isMountedRef, setFullyBlockedOnServerError]
  );

  return (
    <ProcedureFlowchartStateManager
      procedureId={props.procedureGraphQL.id}
      persistedFlowchart={currentProcedure.flowchart}
      procedureMetadata={currentProcedure.metadata}
    >
      {(
        persistedFlowchart,
        draftFlowchart,
        draftFlowchartRef,
        setDraftFlowchart,
        procedureMetadata,
        setProcedureMetadata,
        {dirtyState, readonlyState, isSaveInFlightState, isAutosaverPausedState, isAutosaverBlockedState}
      ) => (
        <PresentationalProcedurePage
          procedureMetadata={procedureMetadata}
          processFlowchartConfigId={currentProcedure.processFlowchartConfigId}
          persistedFlowchart={persistedFlowchart}
          draftFlowchart={draftFlowchart}
          draftFlowchartRef={draftFlowchartRef}
          setDraftFlowchart={setDraftFlowchart}
          setProcedureMetadata={setProcedureMetadata}
          dirtyState={dirtyState}
          readonlyState={readonlyState}
          isSaveInFlightState={isSaveInFlightState}
          isAutosaverPausedState={isAutosaverPausedState}
          isAutosaverBlockedState={isAutosaverBlockedState}
          duplicateProcedure={(params) => duplicateProcedure({...params, currentProcedure})}
          updateAndPersistProcedure={updateProcedure}
          idToCidMap={idToCidMap}
          fullyBlockedOnServerError={fullyBlockedOnServerError}
          onSwitchVersionId={props.onSwitchVersionId}
          publishingRecords={accessibleObjs(props.procedureGraphQL.publishingRecords)}
          currentFlowchartVersionMetadata={{
            ...props.procedureFlowchartVersion,
            isLatest: !props.isViewingOldVersion,
          }}
          lastSavedFlowchartVersionId={lastSavedFlowchartVersionId}
          hardRefresh={props.hardRefresh}
          key={currentProcedure.metadata.id}
        />
      )}
    </ProcedureFlowchartStateManager>
  );
}

interface PresentationalProcedurePageProps {
  procedureMetadata: ProcedureMetadata;
  processFlowchartConfigId: string | null;
  persistedFlowchart: ProcedureFlowchart;
  draftFlowchart: ProcedureFlowchart;
  draftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>;
  setDraftFlowchart: (fc: ProcedureFlowchart) => void;
  setProcedureMetadata: (procedureMetadata: ProcedureMetadata) => void;
  dirtyState: [boolean, (isDirty: boolean) => void];
  readonlyState: [boolean, (isReadonly: boolean) => void];
  isSaveInFlightState: [boolean, (isSaveInFlight: boolean) => void];
  isAutosaverPausedState: [boolean, (isPaused: boolean) => void];
  isAutosaverBlockedState: [boolean, (isBlocked: boolean) => void];
  updateAndPersistProcedure: (
    procedure: ProcedureWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {updatedProcedure: ProcedureWithMetadata; cidToIdMap: Record<string, string>}>;
  duplicateProcedure: (params: {
    name: string;
    templateCollectionId: string;
    procedureType: ProcedureType;
  }) => Promise<void>;
  idToCidMap: Record<string, string>;
  // If the WriteManager determines we should block the user from making changes due to an error.
  // This will make the procedure read-only in its unsaved state and force the user to refresh
  fullyBlockedOnServerError: boolean;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  lastSavedFlowchartVersionId: React.MutableRefObject<string>;
  publishingRecords: Array<PublishingRecordForVersionHistoryPanel>;
  hardRefresh: () => void;
  onSwitchVersionId: (versionId: string) => void;
}

interface AddMethodOptions {
  shouldSwitchViewToFlowchart?: boolean;
  shouldOpenNameEditor?: boolean;
}

/**
 * All about draft state management:
 *
 * We keep three (!) copies of the flowchart in memory. This explains why, and what they are each used for.
 *
 * [1] REDUX
 *
 * This is the source of truth for the flowchart. The other two are just for convenience and/or temporal correctness,
 * as described below. In this component, the Redux store is accessed via the props `draftFlowchart` and `setDraftFlowchart`.
 * These props are provided by ProcedureFlowchartStateManager, which mediates between the UI and Redux.
 *
 * [2] REF
 *
 * The `draftFlowchartRef` prop is also managed by ProcedureFlowchartStateManager. Any changes to the flowchart are
 * immediately saved in the ref, at the same time that they are sent to Redux.
 *
 * The value of using the ref is that it is immune to closure staleness. Even if the ref is stale, the contents of
 * the flowchart are stored one level deeper (ref.current), so changes made to it are available immediately and reliably.
 * This has been necessary to avoid stale flowchart data in `handleSave`, which does a lot of async juggling.
 *
 * [3] LOCAL COMPONENT STATE
 *
 * Finally, the component below has its own local state variable for the draft flowchart. This is needed because
 * we've built out a lot of `setState` calls using the modifier callback form: `setDraftFlowchart(fc => fc)`.
 * Unfortunately, we can't do this with Redux, which requires a hard setter: `setDraftFlowchart(fc)`.
 * In order to keep the modifier callbacks working, we use local state here, and push changes up to Redux
 * in response to those changes via an effect hook.
 *
 * The local state value should not be inspected or used. It is for temporary storage only, until the value
 * can be pushed to Redux.
 *
 * [SUMMARY]
 *
 *    draftFlowchart (redux)         -> √ The source of truth
 *    setDraftFlowchart (redux)      -> X Should not be called directly, other than in response to changes to localDraftFlowchart
 *    localDraftFlowchart (local)    -> X Should not be inspected or used, other than to send to Redux
 *    setLocalDraftFlowchart (local) -> √ Use this on changes to the flowchart
 *    draftFlowchartRef              -> √ Use during saves, or other async operations that require perfect data
 *
 */
export function PresentationalProcedurePage({
  procedureMetadata,
  setProcedureMetadata,
  processFlowchartConfigId,
  persistedFlowchart,
  draftFlowchart,
  draftFlowchartRef,
  setDraftFlowchart: _setDraftFlowchart,
  dirtyState,
  readonlyState,
  isSaveInFlightState,
  isAutosaverPausedState,
  isAutosaverBlockedState,
  updateAndPersistProcedure,
  duplicateProcedure,
  idToCidMap,
  fullyBlockedOnServerError,
  currentFlowchartVersionMetadata,
  lastSavedFlowchartVersionId,
  publishingRecords,
  hardRefresh,
  onSwitchVersionId,
}: PresentationalProcedurePageProps): JSX.Element {
  const {id: procedureId} = procedureMetadata;
  const [_localDraftFlowchartPrivate, _setLocalDraftFlowchartPrivate] =
    useStateIfMounted<ProcedureFlowchart>(persistedFlowchart);
  const [materialFlows, setMaterialFlows] = useStateIfMounted<Array<MaterialFlow>>(
    getMaterialFlows(persistedFlowchart)
  );
  const [isProcedureDirty, setProcedureDirty] = dirtyState;
  const [isProcedureReadOnly, setProcedureReadonly] = readonlyState;
  const [isEditingMethodTab, setIsEditingMethodTab] = useState<boolean>(false);
  const [isSaving, setIsSaving] = isSaveInFlightState;
  const [selectedMethodCid, setSelectedMethodCid] = useState<string | null>(null);
  const tagSchemasForProcedures = useAllTagSchemasWithProceduresShape();
  const tagSchemas = useAllTagSchemasWithStandardShape();
  const bioentitiesForProcedures = useBioentitiesForProcedures(getBioentityIdsFromFlowchart(draftFlowchart));
  const [procedureValidationErrors, setProcedureValidationErrors] = useStateIfMounted<
    Array<ProcedureValidationError>
  >(
    validateProcedure(draftFlowchart, procedureMetadata, {
      tagSchemas,
      tagSchemasForProcedures,
      bioentitiesForProcedures,
    })
  );
  const [accessLevel, setAccessLevel] = useStateIfMounted<null | ProcedureAccessLevel>(null);
  const [anyTabValidationErrors, setAnyTabValidationErrors] = useState(false);
  // Increment this value to force the ProcedureEditor component to be rebuilt by flushing its internal state
  // when staged and unsaved changes are reset via resetDraftFlowchartChanges
  const [procedureEditorKey, setProcedureEditorKey] = useStateIfMounted(0);
  // Downstream components can patch into calls to `saveImmediately`
  const {addMiddleware, removeMiddleware, middleware} = usePreSaveImmediatelyMiddleware();
  const editingMethodNameState = useEditingMethodNameState(draftFlowchart, procedureMetadata.procedureType);

  // Changes to the local draft flowchart are reported to the state manager, who pushes it into redux
  useEffect(() => {
    _setDraftFlowchart(_localDraftFlowchartPrivate);
  }, [_localDraftFlowchartPrivate, _setDraftFlowchart]);

  const setLocalDraftFlowchart = useCallback(
    // Use state updater callback so long running async threads can set draft flowchart state based on the
    // current state instead of stale state from when the async thread started.
    (fcUpdater: (fc: ProcedureFlowchart) => ProcedureFlowchart): void => {
      _setLocalDraftFlowchartPrivate((pendingFc) => {
        const updatedFc = fcUpdater(pendingFc);
        if (
          !isEqual(
            normalizeFlowchartOrderingForComparison(updatedFc),
            normalizeFlowchartOrderingForComparison(persistedFlowchart)
          )
        ) {
          setProcedureDirty(true);
        }
        setMaterialFlows(getMaterialFlows(updatedFc));
        return updatedFc;
      });
    },
    [_setLocalDraftFlowchartPrivate, persistedFlowchart, setMaterialFlows, setProcedureDirty]
  );

  // Create stable refs for saveImmediately to avoid unnecessary re-creation. Without these, the
  // `saveImmediately` function would change whenever we update the callback function `setLocalDraftFlowchart`
  // which does not use `useState`, so it will change whenever the `persistedFlowchart` changes.
  // If we don't do the jump through the `ref`, then we end up recreating the `saveImmediately` function which
  // then causes unnecessarily effects downstream.
  const setLocalDraftFlowchartRef = useRef(setLocalDraftFlowchart);
  const middlewareRef = useRef(middleware);

  useEffect(() => {
    setLocalDraftFlowchartRef.current = setLocalDraftFlowchart;
    middlewareRef.current = middleware;
  }, [setLocalDraftFlowchart, middleware]);

  const resetDraftFlowchartChanges = useCallback((): void => {
    setProcedureDirty(false);
    // Trigger re-render of ProcedureEditor and flush its internal state
    setProcedureEditorKey((c) => c + 1);
    setLocalDraftFlowchart(() => persistedFlowchart);
  }, [setProcedureDirty, setProcedureEditorKey, setLocalDraftFlowchart, persistedFlowchart]);

  // Utility to defer running setDraftFlowchart till pending updates to persistedFlowchart are visible.
  // This is only needed when the code causes a change in persistedFlowchart (eg. refetch) and tries to call
  // setDraftFlowchart within the same synchronous code path. That code would see a stale value for
  // persistedFlowchart.
  const [pendingDraftFlowchartUpdate, setPendingDraftFlowchartUpdate] = useStateIfMounted<{
    updater: (fc: ProcedureFlowchart) => ProcedureFlowchart;
  } | null>(null);

  useEffect(() => {
    if (pendingDraftFlowchartUpdate != null) {
      setLocalDraftFlowchart(pendingDraftFlowchartUpdate.updater);
      setPendingDraftFlowchartUpdate(null);
    }
  }, [pendingDraftFlowchartUpdate, setLocalDraftFlowchart, setPendingDraftFlowchartUpdate]);

  useOnMount(() => {
    AutosaveManager.updateSaveHandler(
      procedureId,
      saveHandlerFactory(
        setProcedureMetadata,
        updateAndPersistProcedure,
        setPendingDraftFlowchartUpdate,
        processFlowchartConfigId
      )
    );

    // On unmount, we immediately swap out to a "clean" save handler, which performs no internal bookkeeping
    return () => {
      AutosaveManager.updateSaveHandler(
        procedureId,
        saveHandlerFactory(
          noop,
          saveProcedureWhenUnmountedFactory(lastSavedFlowchartVersionId.current),
          noop,
          processFlowchartConfigId
        )
      );
    };
  });

  useAllTagSchemasWithStandardShape((schemas) => {
    setAccessLevel(determineProcedureAccessLevel(draftFlowchart, schemas));
  });

  useEffect(() => {
    setWindowTitle(procedureMetadata.name);
  }, [procedureMetadata.name]);

  useEffect(() => {
    const previousValidationErrors = procedureValidationErrors;

    const currentValidationErrors = validateProcedure(draftFlowchart, procedureMetadata, {
      tagSchemas,
      tagSchemasForProcedures,
      bioentitiesForProcedures,
    });
    const newValidationErrors = findNewValidationErrors(previousValidationErrors, currentValidationErrors);
    trackEncounteredConfigurationValidationError(
      procedureId,
      procedureMetadata.procedureType,
      newValidationErrors
    );
    setProcedureValidationErrors(currentValidationErrors);
    // Do not include procedureValidationErrors as a dependency to prevent infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftFlowchart, procedureMetadata, tagSchemas]);

  const isProcedureArchived = isArchived(procedureMetadata);
  const hasAccessToEditConfig = isFeatureEnabledForProcedureType(
    procedureMetadata.procedureType,
    ProceduresFeature.EDIT_CONFIG
  );
  const isViewingPublishedVersion =
    BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING &&
    null != publishingRecords.find((pr) => pr.publishableVersionId === currentFlowchartVersionMetadata.id);

  const readOnlyBannerMessage = getBannerForReadOnlyProcedure({
    procedureType: procedureMetadata.procedureType,
    fullyBlockedOnServerError,
    isLatestFlowchartVersion: currentFlowchartVersionMetadata.isLatest,
    isViewingPublishedVersion,
    isProcedureArchived,
    isWritable: procedureMetadata.permissions.writable,
    hasAccessToEditConfig,
    accessLevel,
  });
  const _newIsProcedureReadOnly = readOnlyBannerMessage != null;

  if (isProcedureReadOnly !== _newIsProcedureReadOnly) {
    setProcedureReadonly(_newIsProcedureReadOnly);
  }

  /**
   * "Blocked" is a fundamental situation. E.g., server error. This should not be controlled downstream.
   * "Paused" is a UX concern. E.g., during a modal editing operation that requires quiet.
   */
  const [shouldPauseAutosave, setShouldPauseAutosave] = isAutosaverPausedState;
  const [_shouldBlockAutosave, setShouldBlockAutosave] = isAutosaverBlockedState;

  useEffect(() => {
    if (fullyBlockedOnServerError) {
      setIsSaving(false);
    }
    setShouldBlockAutosave(
      fullyBlockedOnServerError || isProcedureArchived || !hasAccessToEditConfig || isProcedureReadOnly
    );
  }, [
    fullyBlockedOnServerError,
    isProcedureArchived,
    hasAccessToEditConfig,
    isProcedureReadOnly,
    setIsSaving,
    setShouldBlockAutosave,
  ]);

  /**
   * Use this function when the user takes an action which requires an immediate server call.
   * IMPORTANT: you must make changes to the procedure inside the callback, not before
   * invoking this function! And be sure to apply your updates to the flowchart provided
   * in the callback.
   *
   * GOOD:
   *    saveImmediately((latestFlowchart) => {applyUpdates(latestFlowchart, updates)});
   *
   * BAD:
   *    applyUpdates(props.flowchart, updates);
   *    saveImmediately(noop);
   */
  const saveImmediately = useCallback(
    (
      applyChangesAfterInflightSaves: (latestFlowchart: ProcedureFlowchart) => PersistenceInfo,
      overlayMessage?: string,
      shouldBubbleUpErrors: boolean = true
    ): Promise<ProcedureMetadata> => {
      const prepAfterInflightSaves = (): PersistenceInfo => {
        let middlewareFlowchart = draftFlowchartRef.current;
        for (const {methodCid, fn: preSaveFunction} of middlewareRef.current) {
          const originalMethod = middlewareFlowchart.nodes.find((m) => m.cid === methodCid);
          if (originalMethod != null) {
            const updatedMethod = preSaveFunction(originalMethod);
            middlewareFlowchart = produce(middlewareFlowchart, (fc) => {
              const index = fc.nodes.findIndex((m) => m.cid === methodCid);
              fc.nodes[index] = updatedMethod;
            });
          } else {
            logging.warn('Middleware did not find method', {extra: {methodCid}});
          }
        }
        setLocalDraftFlowchartRef.current(() => middlewareFlowchart);
        return applyChangesAfterInflightSaves(middlewareFlowchart);
      };

      return AutosaveManager.saveImmediately(
        procedureId,
        prepAfterInflightSaves,
        overlayMessage,
        shouldBubbleUpErrors
      );
    },
    [procedureId, draftFlowchartRef]
  );

  const handleRename = useCallback(
    async (newName: string): Promise<void> => {
      const trimmedNewName = newName.trim();
      const isValidName = !isNameEmpty(trimmedNewName);

      if (isValidName) {
        await saveImmediately(() => {
          setProcedureMetadata({...procedureMetadata, name: trimmedNewName});
          return {
            blockingSaveObjectSource: BlockingSaveObjectSource.Metadata,
            blockingSaveOperation: BlockingSaveOperation.Rename,
          };
        }, AutosaveManager.NO_OVERLAY);
      } else {
        notifications.error(`Procedure names must be non-empty`, {duration: 7});
      }
    },
    [procedureMetadata, saveImmediately, setProcedureMetadata]
  );

  useEffect(() => {
    // Displays confirmation on close tab and reload event
    const handleDisplayCloseTabAndReload = (e: BeforeUnloadEvent): void => {
      if (isProcedureDirty) {
        e.preventDefault();
        e.returnValue = ''; // needed to make default pop up show when closing tab
      }
    };

    window.addEventListener('beforeunload', handleDisplayCloseTabAndReload);
    return () => {
      clearNavigationWarnings();
      window.removeEventListener('beforeunload', handleDisplayCloseTabAndReload);
    };
  }, [isProcedureDirty]);

  const handleMove = async (templateCollectionId: string): Promise<void> => {
    logProfilerTimingEvent(PROCEDURE_MOVE_STARTED_EVENT);

    await saveImmediately(
      () => {
        setProcedureMetadata({...procedureMetadata, templateCollectionId});
        return {
          blockingSaveObjectSource: BlockingSaveObjectSource.Metadata,
          blockingSaveOperation: BlockingSaveOperation.Move,
        };
      },
      `Moving ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureMetadata.procedureType, {
        stringCase: StringCase.LOWER,
      })} to different template collection...`
    );

    notifications.success(
      `${getProcedureUIString(
        ProceduresStringKeys.PROCEDURE,
        procedureMetadata.procedureType
      )} was moved succesfully.`
    );
    trackMovedProcedureEvent(
      procedureId,
      procedureMetadata.procedureType,
      procedureMetadata.templateCollectionId,
      templateCollectionId
    );
    const eventMetadata = composeProcedureTypeMetadata(procedureMetadata.procedureType);
    logProfilerTimingEvent(PROCEDURE_MOVE_ENDED_EVENT, eventMetadata);

    // update active template collection shown in the settings sidebar
    navigateToURL(TemplateCollectionDAO.collectionURL(templateCollectionId));
    navigateToURL(procedurePageUrl(procedureId));
  };

  const handleArchive = async (
    itemIds: Array<string>,
    purpose: string,
    options: BulkArchiveFilesOptions
  ): Promise<void> => {
    logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT);
    const archiveAction = (): Promise<void> =>
      new Promise((resolve) => {
        resolve(getArchiveAction(itemIds)(itemIds, purpose, options));
      });
    await withLoadingScreen(bufferForUX(archiveAction()), 'Archiving...');
    const eventMetadata = composeProcedureArchiveUnarchiveEventMetadata(
      true,
      procedureMetadata.procedureType
    );
    logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT, eventMetadata);
    hardRefresh();
  };

  const handleUnarchive = async (): Promise<void> => {
    logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT);
    const unarchiveAction = (): Promise<void> =>
      new Promise((resolve) => {
        resolve(getUnarchiveAction([procedureId])([procedureId]));
      });
    await withLoadingScreen(bufferForUX(unarchiveAction()), 'Unarchiving...');
    const eventMetadata = composeProcedureArchiveUnarchiveEventMetadata(
      false,
      procedureMetadata.procedureType
    );
    logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT, eventMetadata);
    hardRefresh();
  };

  const handleModifyFlowchart = useCallback(
    (cb: ModifyFlowchartCallback): void => {
      setLocalDraftFlowchart(() => cb(draftFlowchartRef.current));
    },
    [setLocalDraftFlowchart, draftFlowchartRef]
  );

  const [currentTab, _setCurrentTab] = useState(MethodEditorTab.DEFINITIONS);

  const handleStopEditingMethod = useCallback((): void => {
    if (selectedMethodCid != null) {
      // Only track event when we've actually switched back to the flowchart view
      trackToggledProcedureViewEvent(
        procedureId,
        procedureMetadata.procedureType,
        ActionOrigin.PROCEDURE_DESIGNER_DETAILS
      );
    }
    setSelectedMethodCid(null);
  }, [procedureId, selectedMethodCid, procedureMetadata.procedureType]);

  const handleStartEditingMethod = (methodCid: string): void => {
    setSelectedMethodCid(methodCid);
    trackToggledProcedureViewEvent(
      procedureId,
      procedureMetadata.procedureType,
      ActionOrigin.PROCEDURE_DESIGNER_FLOWCHART
    );
  };

  const {autofocusEditNameOnNewMethod} = editingMethodNameState;

  const handleAddMethod = useCallback(
    (options: AddMethodOptions): void => {
      if (options?.shouldSwitchViewToFlowchart) {
        handleStopEditingMethod();
      }
      const newMethodCid = generateNewMethodCid();

      handleModifyFlowchart((fc) => {
        const lastNode = fc.nodes.at(-1);
        assert(
          lastNode !== undefined,
          'There should be at least 1 node in flowchart to add from method select dropdown'
        );
        const {updatedFlowchart} = addMethodToLinearFlowchart(
          procedureMetadata.procedureType,
          fc,
          lastNode.cid,
          {
            appendDirection: AppendDirection.DOWNSTREAM,
            newMethodCid,
          }
        );
        if (options?.shouldOpenNameEditor) {
          autofocusEditNameOnNewMethod(newMethodCid);
        }
        return updatedFlowchart;
      });
    },
    [
      procedureMetadata.procedureType,
      handleStopEditingMethod,
      handleModifyFlowchart,
      autofocusEditNameOnNewMethod,
    ]
  );

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const procedurePageContextValue = {
    isProcedureDirty,
    isSaving,
    saveImmediately,
    registerPreSaveImmediatelyMiddleware: addMiddleware,
    unregisterPreSaveImmedatelyMiddleware: removeMiddleware,
    selectedMethodCid,
    setSelectedMethodCid,
    isEditingMethodTab,
    setIsEditingMethodTab,
    shouldPauseAutosave,
    setShouldPauseAutosave,
    idToCidMap,
    materialFlows,
    resetDraftFlowchartChanges,
    accessLevel,
    procedureId,
    procedureType: procedureMetadata.procedureType,
    currentTab,
    dangerouslySetCurrentTab: _setCurrentTab,
    procedureTemplateCollectionOwner: procedureMetadata.templateCollectionOwner,
  };
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const validationContextvalue = {
    procedureValidationErrors,
    anyTabValidationErrors,
    setAnyTabValidationErrors,
  };
  const structuredTableInsertionContextValue = useMemo(
    () => ({itemInsertedFromId: procedureId}),
    [procedureId]
  );

  return (
    <div
      {...getTestProps({component: 'ProcedurePage', element: 'root'})}
      style={{display: 'flex', flexDirection: 'column', height: '100%'}}
    >
      <ProcedurePageContext.Provider value={procedurePageContextValue}>
        <ProcedureValidationContext.Provider value={validationContextvalue}>
          <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
            <ProcedureEditorHeader
              procedureMetadata={procedureMetadata}
              methodNodes={draftFlowchart.nodes}
              onDuplicate={duplicateProcedure}
              onRename={handleRename}
              onMove={handleMove}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onAddMethod={() => {
                handleAddMethod({shouldSwitchViewToFlowchart: true, shouldOpenNameEditor: true});
              }}
              currentFlowchartVersionMetadata={currentFlowchartVersionMetadata}
              hardRefresh={hardRefresh}
              onSwitchVersionId={onSwitchVersionId}
              isProcedureReadOnly={isProcedureReadOnly}
              key={procedureId}
            />
            {readOnlyBannerMessage && <BufferOptions banner={readOnlyBannerMessage} fullScreen={false} />}
            {BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION && processFlowchartConfigId != null ? (
              <ProcessFlowchartConfigEditor
                flowchartTypeName={ProcedureProcessesPlugin.FLOWCHART_TYPE_NAME}
                flowchartConfigId={processFlowchartConfigId}
                locked={false}
              />
            ) : (
              <ProcedureEditor
                flowchart={draftFlowchartRef.current}
                modifyFlowchart={handleModifyFlowchart}
                procedureId={procedureMetadata.id}
                templateCollectionId={procedureMetadata.templateCollectionId}
                isProcedureReadOnly={isProcedureReadOnly}
                isProcedureArchived={isProcedureArchived}
                procedureEditorKey={procedureEditorKey}
                onStopEditingMethod={handleStopEditingMethod}
                onStartEditingMethod={handleStartEditingMethod}
                editingMethodNameState={editingMethodNameState}
              />
            )}
            <ProcedureContextRetriever pageContextValue={procedurePageContextValue} />
          </StructuredTableInsertionContext.Provider>
        </ProcedureValidationContext.Provider>
      </ProcedurePageContext.Provider>
    </div>
  );
}

// Used for testing only
function ProcedureContextRetriever(props: {pageContextValue: ProcedurePageContextValue}): null {
  return null;
}

const proceduresPlugin = new ProcedureProcessesPlugin();
proceduresPlugin.registerForEditor();
